import _ from 'lodash';
import moment from 'moment';

import {TOTAL, OCCUPANCY_STATUSES, REPORT_SHAPE} from 'ducks/occupancy';
import {DATE_FORMAT_API} from 'utils/datetime';
import {removeRevenueSegments} from 'utils/occupancy';

export const buildPhysicalBookingTypeColumn = column => ({
  date: moment(column.date, DATE_FORMAT_API),
  barValues: buildBookingTypeBarValues(column),
  tableData: buildBookingTypeColumnTableData(column)
});

const buildBookingTypeBarValues = column => {
  const roomTypeGroups = _.keys(column.report.roomTypeGroupBreakdown);

  /*
   Result format:
   {
     'Co-living': {
        value: 1000,
        maxValue: 1000000,
        tooltipData: [
            {
              label: `Total Co-living booked (of total building)`,
              value: 10000,
              maxValue: 20000
            },
            {
              label: 'Confirmed',
              value: 1000,
              maxValue: 2000
            },
            {
              label: 'Reserved',
              value: 1000,
              maxValue: 2000
            }
        ]
     },
     'Corporate': {
        value: 1000,
        maxValue: 1000000,
        tooltipData: [
            {
              label: `Total Co-living booked (of total building)`,
              value: 10000,
              maxValue: 20000
            },
            {
              label: 'Confirmed',
              value: 1000,
              maxValue: 2000
            },
            {
              label: 'Reserved',
              value: 1000,
              maxValue: 2000
            }
        ]
     }
     ...
   }
  */
  return _.reduce(
    roomTypeGroups,
    (result, roomTypeGroupName) => ({
      ...result,
      [roomTypeGroupName]: buildBookingTypeBarValue(
        roomTypeGroupName,
        column.report.roomTypeGroupBreakdown[roomTypeGroupName].total,
        column
      )
    }),
    {}
  );
};

const buildBookingTypeBarValue = (roomTypeGroup, report, column) => {
  const unifiedTotals = unifyTotals(report);
  const longStayRooms = _.get(
    column.report.total.real,
    OCCUPANCY_STATUSES.LONG_STAY_ROOMS,
    0
  );

  return {
    value: _.get(unifiedTotals, OCCUPANCY_STATUSES.PHYSICAL_BOOKED, 0),
    maxValue: longStayRooms,
    tooltipData: [
      {
        label: `Total ${roomTypeGroup} booked (of total building)`,
        value: _.get(unifiedTotals, OCCUPANCY_STATUSES.PHYSICAL_BOOKED, 0),
        maxValue: longStayRooms
      },
      {
        label: 'Confirmed',
        value: _.get(unifiedTotals, OCCUPANCY_STATUSES.CONFIRMED, 0),
        maxValue: _.get(unifiedTotals, OCCUPANCY_STATUSES.PHYSICAL_BOOKED, 0)
      },
      {
        label: 'Reserved',
        value: _.get(unifiedTotals, OCCUPANCY_STATUSES.RESERVED, 0),
        maxValue: _.get(unifiedTotals, OCCUPANCY_STATUSES.PHYSICAL_BOOKED, 0)
      }
    ]
  };
};

const unifyTotals = totals => ({
  physicalBooked:
    totals.real.physicalBooked +
    totals.dummy.physicalBooked +
    totals.real.corporateOccupancy,
  confirmed:
    totals.real.confirmed +
    totals.dummy.confirmed +
    totals.real.corporateOccupancy,
  reserved: totals.real.reserved + totals.dummy.reserved,
  available: totals.real.available,
  outOfOrder: totals.real.outOfOrder,
  totalRooms: totals.real.totalRooms,
  longStayRooms: totals.real.longStayRooms
});

const buildBookingTypeColumnTableData = column => {
  const totalsOccupancy = column.report.total;

  const roomTypeGroups = _.keys(column.report.roomTypeGroupBreakdown);

  const totalsRoomsBreakDown = _.flatten(
    _.map(roomTypeGroups, rtg =>
      _.values(column.report.roomTypeGroupBreakdown[rtg].roomTypeBreakdown)
    )
  );

  const roomTypesColumnTableData = _.reduce(
    roomTypeGroups,
    (result, roomTypeGroupName) => {
      const groupValue = buildBookingTypeHeaderRow(
        column.report.roomTypeGroupBreakdown[roomTypeGroupName].total,
        _.values(
          column.report.roomTypeGroupBreakdown[roomTypeGroupName]
            .roomTypeBreakdown
        )
      );
      const roomTypes = buildBookingTypeInnerRows(
        column.report.roomTypeGroupBreakdown[roomTypeGroupName]
          .roomTypeBreakdown
      );

      return {
        ...result,
        [roomTypeGroupName]: {
          groupValue,
          nestedElements: roomTypes
        }
      };
    },
    {}
  );
  /*
     Result format:
     {
       'Totals': {
         value: 1000,
         maxValue: 100000,
         tooltipData: [
           {
             label: 'Confirmed',
             value: 200,
             maxValue: 300
           },
           {
             label: 'Reserved',
             value: 200,
             maxValue: 300
           },
           {
             label: 'Confirmed (dummy)',
             value: 200,
             maxValue: 300
           },
           {
             label: 'Reserved (dummy)',
             value: 200,
             maxValue: 300
           }
         ]
       }
     }
    */
  return {
    [TOTAL]: buildBookingTypeHeaderRow(totalsOccupancy, totalsRoomsBreakDown),
    ...roomTypesColumnTableData
  };
};

const buildBookingTypeHeaderRow = (occupancy, roomsBreakDown) => ({
  value:
    _.get(occupancy.real, OCCUPANCY_STATUSES.PHYSICAL_BOOKED, 0) +
    _.get(occupancy.dummy, OCCUPANCY_STATUSES.PHYSICAL_BOOKED, 0) +
    _.get(occupancy.real, 'corporateOccupancy', 0),
  maxValue: _.get(occupancy.real, OCCUPANCY_STATUSES.TOTAL_ROOMS, 0),
  displayValue: _.get(occupancy.real, OCCUPANCY_STATUSES.LONG_STAY_ROOMS, 0),
  tooltipData: [
    {
      label: 'Confirmed',
      value: _.sumBy(
        roomsBreakDown,
        (x: any) =>
          _.get(x.real, OCCUPANCY_STATUSES.CONFIRMED, 0) +
          _.get(x.real, 'corporateOccupancy', 0)
      ),
      maxValue: _.get(occupancy.real, OCCUPANCY_STATUSES.LONG_STAY_ROOMS, 0)
    },
    {
      label: 'Reserved',
      value: _.sumBy(roomsBreakDown, (x: any) =>
        _.get(x.real, OCCUPANCY_STATUSES.RESERVED, 0)
      ),
      maxValue: _.get(occupancy.real, OCCUPANCY_STATUSES.LONG_STAY_ROOMS, 0)
    },
    {
      label: 'Confirmed (dummy)',
      value: _.sumBy(roomsBreakDown, (x: any) =>
        _.get(x.dummy, OCCUPANCY_STATUSES.CONFIRMED, 0)
      ),
      maxValue: _.get(occupancy.real, OCCUPANCY_STATUSES.LONG_STAY_ROOMS, 0)
    },
    {
      label: 'Reserved (dummy)',
      value: _.sumBy(roomsBreakDown, (x: any) =>
        _.get(x.dummy, OCCUPANCY_STATUSES.RESERVED, 0)
      ),
      maxValue: _.get(occupancy.real, OCCUPANCY_STATUSES.LONG_STAY_ROOMS, 0)
    }
  ]
});

const buildBookingTypeInnerRows = roomTypeGroup => {
  return _.mapValues(roomTypeGroup, roomType => ({
    label: roomType.label,
    value:
      roomType.real[OCCUPANCY_STATUSES.PHYSICAL_BOOKED] +
      roomType.dummy[OCCUPANCY_STATUSES.PHYSICAL_BOOKED] +
      roomType.real['corporateOccupancy'],
    maxValue: roomType.real[OCCUPANCY_STATUSES.LONG_STAY_ROOMS],
    tooltipData: [
      {
        label: 'Confirmed',
        value:
          roomType.real[OCCUPANCY_STATUSES.CONFIRMED] +
          roomType.real['corporateOccupancy'],
        maxValue: roomType.real[OCCUPANCY_STATUSES.LONG_STAY_ROOMS]
      },
      {
        label: 'Reserved',
        value: roomType.real[OCCUPANCY_STATUSES.RESERVED],
        maxValue: roomType.real[OCCUPANCY_STATUSES.LONG_STAY_ROOMS]
      },
      {
        label: 'Confirmed (dummy)',
        value: roomType.dummy[OCCUPANCY_STATUSES.CONFIRMED],
        maxValue: roomType.real[OCCUPANCY_STATUSES.LONG_STAY_ROOMS]
      },
      {
        label: 'Reserved (dummy)',
        value: roomType.dummy[OCCUPANCY_STATUSES.RESERVED],
        maxValue: roomType.real[OCCUPANCY_STATUSES.LONG_STAY_ROOMS]
      }
    ]
  }));
};

export const buildPhysicalBookingTypeTableHeaderData = columns => {
  /*
    Result format:
    [
      {
        label: 'Co-living',
        nestedElements: [
          {
            label: 'Shared Ensuite',
            totalRooms: 500,
            value: 'shensuite'
          }
        ],
        totalRooms: 500,
        value: 'Co-living',
      },
      ...
    ]
  */
  if (_.isEmpty(columns)) {
    return [];
  }
  const buildForColumn = column => {
    return _.map(column.report.roomTypeGroupBreakdown, (value, key) => ({
      label: key,
      value: key,
      totalRooms: value.total.real.longStayRooms,
      nestedElements: _.map(value.roomTypeBreakdown, (value, key) => ({
        label: value.label,
        value: key,
        totalRooms: value.real.totalRooms
      }))
    }));
  };

  // This builds the leftmost values "total" for each row
  return buildForColumn(columns[0]);
};

export const buildPhysicalRoomStatusColumn = column => ({
  date: moment(column.date, DATE_FORMAT_API),
  barValues: buildRoomStatusBarValues(column),
  tableData: buildRoomStatusColumnTableData(column)
});

const reshapeOccupancyReport = occupancyReport => {
  const unifiedTotals = {
    [TOTAL]: unifyTotals(occupancyReport.total),
    ..._.mapValues(occupancyReport.roomTypeGroupBreakdown, value =>
      unifyTotals(value.total)
    )
  };

  /* Transforms the following data:
  ```
  {'Co-living':{'available': 8, 'booked': 476, 'confirmed': 199,
                'outOfOrder': 0, 'reserved': 263, 'total': 470},
   'Corporate': {'available': 0, 'booked': 75, 'confirmed': 75,
                 'outOfOrder': 0, 'reserved': 0, 'total': 75},
   'Guest': {'available': 4, 'booked': 0, 'confirmed': 0,
             'outOfOrder': 0, 'reserved': 0, 'total': 4}}}
  ```
  Into:
  ```
  {'available': {'Co-living': 8, 'Corporate': 0, 'Guest': 4},
   'booked': {'Co-living': 476, 'Corporate': 75, 'Guest': 0},
   'confirmed': {'Co-living': 199, 'Corporate': 75, 'Guest': 0},
   'outOfOrder': {'Co-living': 0, 'Corporate': 0, 'Guest': 0},
   'reserved': {'Co-living': 263, 'Corporate': 0, 'Guest': 0},
   'total': {'Co-living': 470, 'Corporate': 75, 'Guest': 4}}}
  ```
  */
  return _.mapKeys(
    _.mapValues(OCCUPANCY_STATUSES, status =>
      _.mapValues(unifiedTotals, bookingTypeReport => bookingTypeReport[status])
    ),
    (_value, key) => OCCUPANCY_STATUSES[key]
  );
};

const buildRoomStatusBarData = column => {
  const reshapedOccupancyReport = reshapeOccupancyReport(column.report);

  const totals = unifyTotals(column.report.total);

  const confirmed = reshapedOccupancyReport[OCCUPANCY_STATUSES.CONFIRMED];
  const reserved = reshapedOccupancyReport[OCCUPANCY_STATUSES.RESERVED];
  const outOfOrder = reshapedOccupancyReport[OCCUPANCY_STATUSES.OUT_OF_ORDER];

  return {
    [OCCUPANCY_STATUSES.CONFIRMED]: buildRoomStatusValue(
      OCCUPANCY_STATUSES.CONFIRMED,
      confirmed,
      totals
    ),
    [OCCUPANCY_STATUSES.RESERVED]: buildRoomStatusValue(
      OCCUPANCY_STATUSES.RESERVED,
      reserved,
      totals
    ),
    [OCCUPANCY_STATUSES.OUT_OF_ORDER]: buildRoomStatusValue(
      OCCUPANCY_STATUSES.OUT_OF_ORDER,
      outOfOrder,
      totals
    )
  };
};

const buildRoomStatusBarValues = column =>
  _.mapValues(buildRoomStatusBarData(column), values => values.groupValue);

const buildRoomStatusColumnTableData = column => {
  const builtBarData = buildRoomStatusBarData(column);
  return {
    [TOTAL]: buildRoomStatusTotalHeaderRow(column),
    [OCCUPANCY_STATUSES.CONFIRMED]: builtBarData[OCCUPANCY_STATUSES.CONFIRMED],
    [OCCUPANCY_STATUSES.RESERVED]: builtBarData[OCCUPANCY_STATUSES.RESERVED],
    [OCCUPANCY_STATUSES.OUT_OF_ORDER]:
      builtBarData[OCCUPANCY_STATUSES.OUT_OF_ORDER]
  };
};

const buildRoomStatusValue = (roomStatus, data, totals) => {
  const roomStatusData = removeRevenueSegments(
    REPORT_SHAPE.ROOM_STATUS.segments
  ).filter(s => s.value === roomStatus)[0];
  const roomTypeGroups = _.keys(data).filter(el => el !== 'Total');

  const roomTypeTooltipData = _.reduce(
    roomTypeGroups,
    (result, roomTypeGroupName) => {
      const roomTypeGroupNameData = data[roomTypeGroupName];

      return [
        ...result,
        {
          label: roomTypeGroupName,
          value: _.isObject(roomTypeGroupNameData)
            ? roomTypeGroupNameData.total
            : roomTypeGroupNameData,
          maxValue: totals[OCCUPANCY_STATUSES.LONG_STAY_ROOMS]
        }
      ];
    },
    []
  );

  const roomStatusNestedElements = _.reduce(
    roomStatusData.nestedElements,
    (result, nestedElement) => ({
      ...result,
      [nestedElement.value]: {
        label: nestedElement.value,
        value: totals[roomStatus][nestedElement.value],
        maxValue: totals[OCCUPANCY_STATUSES.TOTAL_ROOMS]
      }
    }),
    {}
  );

  const roomStatusTotal = _.isObject(totals[roomStatus])
    ? totals[roomStatus].total
    : totals[roomStatus];

  return {
    groupValue: {
      value: roomStatusTotal,
      maxValue: totals[OCCUPANCY_STATUSES.LONG_STAY_ROOMS],
      displayValue: totals[OCCUPANCY_STATUSES.LONG_STAY_ROOMS],
      tooltipData: [
        {
          label: `Total ${roomStatusData.label} (of total building)`,
          value: roomStatusTotal,
          maxValue: totals[OCCUPANCY_STATUSES.LONG_STAY_ROOMS]
        },
        ...roomTypeTooltipData
      ]
    },
    nestedElements: roomStatusNestedElements
  };
};

const buildRoomStatusTotalHeaderRow = column => {
  const totals = unifyTotals(column.report.total);
  const roomTypeGroups = _.keys(column.report.roomTypeGroupBreakdown);

  const roomTypeTooltipData = roomTypeGroups.map(label => ({
    label,
    value: _.get(
      unifyTotals(column.report.roomTypeGroupBreakdown[label].total),
      OCCUPANCY_STATUSES.PHYSICAL_BOOKED,
      0
    ),
    maxValue: totals[OCCUPANCY_STATUSES.LONG_STAY_ROOMS]
  }));

  return {
    value: totals[OCCUPANCY_STATUSES.PHYSICAL_BOOKED],
    maxValue: totals[OCCUPANCY_STATUSES.TOTAL_ROOMS],
    displayValue: totals[OCCUPANCY_STATUSES.LONG_STAY_ROOMS],
    tooltipData: [
      {
        label: TOTAL,
        value: totals[OCCUPANCY_STATUSES.PHYSICAL_BOOKED],
        maxValue: totals[OCCUPANCY_STATUSES.LONG_STAY_ROOMS]
      },
      ...roomTypeTooltipData
    ]
  };
};

export const buildPhysicalRoomStatusTableHeaderData = () => {
  return removeRevenueSegments(REPORT_SHAPE.ROOM_STATUS.segments);
};
